import { Lock, Trash2 } from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { IconButton } from "@/components/ui/icon-button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatRelative } from "@/lib/format-date"

import {
  accountStatus,
  ROLE_OPTIONS,
  type AccountStatus,
  type ConsoleRole,
  type ConsoleUser,
  type ServiceKey,
} from "./users-keys-data"

export function UserAccountCell({ user }: { user: ConsoleUser }): JSX.Element {
  const status = accountStatus(user)
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={user.name ?? user.email} identity={user.id} size="md" />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-body font-medium text-text">
            {user.name ?? user.email}
          </span>
          {user.isBootstrap && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-surface-sunken px-2 py-0.5 text-caption font-medium text-text-2 ring-1 ring-inset ring-border">
              Bootstrap
            </span>
          )}
          <StatusPill status={status} />
        </div>
        {user.name && (
          <div className="mt-0.5 truncate font-id text-caption text-text-3">{user.email}</div>
        )}
      </div>
    </div>
  )
}

function StatusPill({ status }: { status: AccountStatus }): JSX.Element | null {
  if (status === "active") return null
  const tone = status === "deactivated" ? "danger" : "print"
  const label =
    status === "pending-invite"
      ? "Invited"
      : status === "never-signed-in"
        ? "Never signed in"
        : "Deactivated"
  const classes =
    tone === "danger"
      ? "bg-surface-sunken text-text-3 ring-border"
      : "bg-print-tint text-print ring-print/20"
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-caption font-medium ring-1 ring-inset ${classes}`}
    >
      {label}
    </span>
  )
}

export function RoleCell({
  user,
  onChange,
  disabled,
}: {
  user: ConsoleUser
  onChange: (role: ConsoleRole) => void
  disabled: boolean
}): JSX.Element {
  return (
    <Select
      value={user.role}
      onValueChange={(v) => onChange(v as ConsoleRole)}
      disabled={disabled}
    >
      <SelectTrigger size="sm" className="w-[140px]" aria-label={`Role for ${user.email}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="w-[320px]" align="start">
        {ROLE_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} description={opt.description}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function LastSeenCell({ iso }: { iso: string | null }): JSX.Element {
  if (iso === null) return <span className="text-body-sm text-text-3">—</span>
  return (
    <span className="font-id tabular-nums text-body-sm text-text-3">{formatRelative(iso)}</span>
  )
}

export function RemoveUserCell({
  user,
  onRemove,
}: {
  user: ConsoleUser
  onRemove: () => void
}): JSX.Element {
  if (user.isBootstrap) {
    return (
      <span
        title="The bootstrap account cannot be removed from this screen."
        className="inline-flex items-center text-text-3"
      >
        <Lock className="size-4" aria-hidden="true" />
      </span>
    )
  }
  return (
    <IconButton
      size="sm"
      variant="ghost"
      label={`Remove ${user.email}`}
      icon={<Trash2 />}
      onClick={onRemove}
    />
  )
}

export function KeyCell({ item }: { item: ServiceKey }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="truncate text-body font-medium text-text">{item.name}</span>
        {item.fromEnv && (
          <span
            title="Seeded by PYXIS_API_KEYS at boot"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-surface-sunken px-2 py-0.5 text-caption font-medium text-text-2 ring-1 ring-inset ring-border"
          >
            <Lock className="size-3" aria-hidden="true" />
            from env
          </span>
        )}
      </div>
      <div className="mt-0.5 truncate font-id text-caption text-text-3">
        {item.prefix}
        {"·".repeat(20)}
      </div>
    </div>
  )
}

export function CreatedCell({ item }: { item: ServiceKey }): JSX.Element {
  return (
    <div className="min-w-0 text-body-sm">
      <div className="font-id tabular-nums text-text-2">{formatRelative(item.createdAt)}</div>
      <div className="mt-0.5 truncate text-caption text-text-3">by {item.createdBy}</div>
    </div>
  )
}

export function LastUsedCell({ iso }: { iso: string | null }): JSX.Element {
  if (iso === null) return <span className="text-body-sm text-text-3">Never used</span>
  return (
    <span className="font-id tabular-nums text-body-sm text-text">{formatRelative(iso)}</span>
  )
}

export function RevokeKeyCell({
  item,
  onRevoke,
}: {
  item: ServiceKey
  onRevoke: () => void
}): JSX.Element {
  if (item.fromEnv) {
    return (
      <span
        title="Remove from PYXIS_API_KEYS and restart to revoke."
        className="inline-flex items-center text-text-3"
      >
        <Lock className="size-4" aria-hidden="true" />
      </span>
    )
  }
  return (
    <IconButton
      size="sm"
      variant="ghost"
      label={`Revoke ${item.name}`}
      icon={<Trash2 />}
      onClick={onRevoke}
    />
  )
}
